import type { CounterpartyMatchSignals, CounterpartyRuleConflict, NameMatchListItem } from '@/api/contracts/apiContracts';
import type { TranslationKey } from './types';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';
type TranslateFn = (key: TranslationKey, params?: Record<string, string | number>) => string;

export function createMatchingLabelHelpers(t: TranslateFn) {
  return {
    mapMatchStatus(status: string): { label: string; tone: Tone } {
      switch (status) {
        case 'Unmatched':
          return { label: t('matching.status.unmatched'), tone: 'warning' };
        case 'LowConfidence':
          return { label: t('matching.status.lowConfidence'), tone: 'warning' };
        case 'AutoMatched':
          return { label: t('matching.status.autoMatched'), tone: 'success' };
        case 'ManualMatched':
          return { label: t('matching.status.manualMatched'), tone: 'info' };
        case 'Excluded':
          return { label: t('matching.status.excluded'), tone: 'neutral' };
        default:
          return { label: status, tone: 'neutral' };
      }
    },

    mapCounterpartyMatchType(matchType: string): { label: string; tone: 'neutral' | 'info' } {
      switch (matchType) {
        case 'ExactRule':
        case 'ContainsRule':
          return { label: t('matching.matchType.rule'), tone: 'info' };
        case 'NormalizedRule':
          return { label: t('matching.matchType.normalized'), tone: 'info' };
        case 'FuzzyCandidate':
          return { label: t('matching.matchType.candidate'), tone: 'neutral' };
        case 'ManualMatched':
          return { label: t('matching.matchType.manual'), tone: 'info' };
        case 'Unmatched':
          return { label: t('matching.matchType.unmatched'), tone: 'neutral' };
        case 'Excluded':
          return { label: t('matching.matchType.excluded'), tone: 'neutral' };
        default:
          return { label: matchType, tone: 'neutral' };
      }
    },

    formatMatchedSignals(signals: CounterpartyMatchSignals): string[] {
      const items: string[] = [];
      if (signals.rulePattern) items.push(`${t('matching.signal.rule')}: ${signals.rulePattern}`);
      if (signals.normalizedRulePattern) {
        items.push(`${t('matching.signal.normalizedRule')}: ${signals.normalizedRulePattern}`);
      }
      if (signals.normalizedBankName) {
        items.push(`${t('matching.signal.normalizedBankName')}: ${signals.normalizedBankName}`);
      }
      if (signals.priority != null) items.push(`${t('matching.signal.priority')}: ${signals.priority}`);
      if (signals.exactMatch) items.push(t('matching.signal.exactMatch'));
      if (signals.normalizedMatch) items.push(t('matching.signal.normalizedMatch'));
      if (signals.containsMatch) items.push(t('matching.signal.containsMatch'));
      if (signals.manualSelection) items.push(t('matching.signal.manualSelection'));
      if (signals.excludeCategory) {
        items.push(`${t('matching.signal.excludeCategory')}: ${signals.excludeCategory}`);
      }
      if (signals.excludeComment) {
        items.push(`${t('matching.signal.excludeComment')}: ${signals.excludeComment}`);
      }
      return items;
    },

    getActionLabel(_row: NameMatchListItem, displayStatus: string): string {
      if (displayStatus === 'Unmatched') return t('matching.action.match');
      if (displayStatus === 'LowConfidence') return t('matching.action.review');
      if (displayStatus === 'Excluded') return t('matching.action.unexclude');
      return t('matching.action.edit');
    },

    mapConflictTypeLabel(type: CounterpartyRuleConflict['type']): string {
      switch (type) {
        case 'exactDuplicate':
          return t('matching.wizard.conflict.exactDuplicate');
        case 'duplicateScopeDifferentCounterparty':
          return t('matching.wizard.conflict.duplicateScope');
        case 'overlappingPattern':
          return t('matching.wizard.conflict.overlapping');
        case 'priorityShadowing':
          return t('matching.wizard.conflict.priorityShadowing');
        case 'differentSyrveMapping':
          return t('matching.wizard.conflict.differentMapping');
        default:
          return t('matching.wizard.conflict.default');
      }
    },

    wizardStepLabel(step: 'review' | 'select-counterparty' | 'rule-decision' | 'complete'): string {
      switch (step) {
        case 'review':
          return t('matching.wizard.step.transaction');
        case 'select-counterparty':
          return t('matching.wizard.step.counterparty');
        case 'rule-decision':
          return t('matching.wizard.step.rule');
        case 'complete':
          return t('matching.wizard.step.done');
      }
    },
  };
}

export type MatchingLabelHelpers = ReturnType<typeof createMatchingLabelHelpers>;
